/* eslint-disable no-bitwise */
// IEEE 754 half-precision <-> single-precision conversion. Used to read fp16
// patch embeddings out of the vision-bot tensor without depending on the
// non-standard Float16Array typed array.

const FP16_TO_FP32_VIEW = new ArrayBuffer(4);
const FP16_TO_FP32_F32 = new Float32Array(FP16_TO_FP32_VIEW);
const FP16_TO_FP32_U32 = new Uint32Array(FP16_TO_FP32_VIEW);

export function fp16ToFp32(half: number): number {
  const sign = (half & 0x8000) >>> 15;
  const exp = (half & 0x7c00) >>> 10;
  const frac = half & 0x03ff;

  let f32: number;

  if (exp === 0) {
    if (frac === 0) {
      f32 = sign << 31;
    } else {
      // Subnormal — renormalize.
      let mantissa = frac;
      let exponent = -14;

      while ((mantissa & 0x0400) === 0) {
        mantissa <<= 1;
        exponent -= 1;
      }

      mantissa &= 0x03ff;
      f32 = (sign << 31) | ((exponent + 127) << 23) | (mantissa << 13);
    }
  } else if (exp === 0x1f) {
    f32 = (sign << 31) | (0xff << 23) | (frac << 13);
  } else {
    f32 = (sign << 31) | ((exp - 15 + 127) << 23) | (frac << 13);
  }

  FP16_TO_FP32_U32[0] = f32 >>> 0;

  return FP16_TO_FP32_F32[0];
}

const FP32_TO_FP16_VIEW = new ArrayBuffer(4);
const FP32_TO_FP16_F32 = new Float32Array(FP32_TO_FP16_VIEW);
const FP32_TO_FP16_U32 = new Uint32Array(FP32_TO_FP16_VIEW);

export function fp32ToFp16(value: number): number {
  FP32_TO_FP16_F32[0] = value;
  const f32 = FP32_TO_FP16_U32[0];
  const sign = (f32 >>> 31) & 0x1;
  const exp = (f32 >>> 23) & 0xff;
  const frac = f32 & 0x7fffff;

  if (exp === 0xff) {
    return (sign << 15) | 0x7c00 | (frac !== 0 ? 0x200 : 0);
  }

  const newExp = exp - 127 + 15;

  if (newExp >= 0x1f) {
    return (sign << 15) | 0x7c00;
  }

  if (newExp <= 0) {
    if (newExp < -10) {
      return sign << 15;
    }

    const mantissa = (frac | 0x800000) >>> (1 - newExp + 13);

    return (sign << 15) | mantissa;
  }

  return (sign << 15) | (newExp << 10) | (frac >>> 13);
}

// Pack a Float32Array into IEEE-754 half-precision little-endian bytes,
// matching numpy `np.frombuffer(buf, dtype=np.float16)` on the vision-bot side.
// Both the routine-runner host and vision-bot run little-endian.
export function fp32ArrayToFp16Bytes(values: Float32Array): Uint8Array {
  const u16 = new Uint16Array(values.length);

  for (let i = 0; i < values.length; i += 1) {
    u16[i] = fp32ToFp16(values[i]);
  }

  return new Uint8Array(u16.buffer, u16.byteOffset, u16.byteLength);
}

export function uint8BytesToUint16(values: Uint8Array): Uint16Array {
  if (values.byteLength % 2 !== 0) {
    throw new Error(
      `fp16 byte buffer length must be even, got ${values.byteLength}`,
    );
  }

  // Copy into a new ArrayBuffer so the Uint16Array view is independent of any
  // ambient byte offset on the input.
  const out = new Uint16Array(values.byteLength / 2);

  const view = new DataView(
    values.buffer,
    values.byteOffset,
    values.byteLength,
  );

  for (let i = 0; i < out.length; i += 1) {
    out[i] = view.getUint16(i * 2, true);
  }

  return out;
}

// Inverse of fp32ArrayToFp16Bytes: decode little-endian fp16 bytes into a
// Float32Array. Safe for unaligned byteOffsets (see uint8BytesToUint16).
export function fp16BytesToFp32Array(bytes: Uint8Array): Float32Array {
  const fp16 = uint8BytesToUint16(bytes);
  const out = new Float32Array(fp16.length);

  for (let i = 0; i < fp16.length; i += 1) {
    out[i] = fp16ToFp32(fp16[i]);
  }

  return out;
}
